<template>
  <div>
    <tool-bar :title="title"></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <div class="margin_top_20" v-if="showUI">
      <div class="input_container">
        <weex-text class="input_label">{{getFieldLabel('office_name')}}</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('office_name')"
          @input="onInputEntered('office_name', $event.target.value)"
          return-key-type="next">
        <field-error-item
          :show-error="showFieldError('office_name')"
          :error-msg="getErrorMsg('office_name')"
        ></field-error-item>
      </div>

      <div
        class="ezcred-checkbox-div margin-left-10 margin-right-10 margin_top_20"
        @click="onGSTPresentCheckboxChanged">
        <weex-image
          :src="checkboxImage"
          class="ezcred-checkbox-image"
        ></weex-image>
        <weex-text class="ezcred-checkbox-text">{{ gstPresentText }}</weex-text>
      </div>

      <div v-if="gstPresent">
        <div class="input_container">
          <weex-text class="input_label">{{getFieldLabel('gst_number')}}</weex-text>
          <input
            class="input_with_border_bottom"
            :value="getFieldValue('gst_number')"
            @input="onGSTEntered($event.target.value)"
            return-key-type="next">
          <field-error-item
            :show-error="showFieldError('gst_number')"
            :error-msg="getErrorMsg('gst_number')"
          ></field-error-item>
        </div>

        <file-attach-button
          class="horizontal_center margin_top_40"
          size="medium"
          file-attach-button-id="upload_gst"
          :show-attach-description="showFieldError('gst_copy_uploaded')"
          :attach-description="getErrorMsg('gst_copy_uploaded')"
          :text="uploadGstCopyText"
          :attach-image-des-src="documentAttachImage"
          :enabled-text-style="attachFileButtonTextStyle"
          :enabled-btn-style="attachFileButtonStyle"
          @filesAttached="onGSTAttached"
        ></file-attach-button>
      </div>

      <wxc-button
        text="Next"
        class="margin-top-40 margin-bottom-20 horizontal_center"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import ToolBar from "../layout/ToolBar";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import Loading from "../layout/Loading";
import StageCommentItem from "../layout/StageCommentItem";
import { Activities } from "../../utils/ActivityNavigator";
import DataSource from "../../data/DataSource";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import FileAttachButton from "../layout/FileAttachButton";
import { ImageResourcesUtil } from "../../utils/ImageResourcesUtil";
import AlertService from "../core/alert/AlertService";
import { EntityType } from "../../utils/EntityType";
import { DocumentDescription } from "../../utils/DocumentDetails";
import { ValidationPatterns } from "../../utils/ValidationPatterns";
import DynamicFieldUtils, { FieldTypes } from "../../utils/DynamicFieldUtils";
import FieldErrorItem from "../layout/FieldErrorItem";
import AttributeUtil, { AttributeDetails } from "../../utils/AttributeUtil";
import RefreshItem from "../layout/RefreshItem";
import LoanTasksUtility from '../../utils/LoanTasksUtility';

const DOCUMENT_UPLOADED = "Document uploaded successfully";

const ALL_FIELDS_DATA = {
  office_name: {
    label: "Business Name",
    validation: "",
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter Business Name",
    optional: false
  },

  gst_number: {
    label: "GST Number",
    validation: ValidationPatterns.GST_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid GST Number",
    optional: false
  },

  gst_copy_uploaded: {
    validation: "",
    fieldType: FieldTypes.BUTTON,
    errorMsg: "<div style='color: red; font-size: 14px; text-align: center'>Please upload GST Copy</div>",
    optional: false
  }
};

export default {
  name: "GSTCopyActivity",
  components: {
    RefreshItem,
    ActivityRedirectItem,
    ToolBar,
    ErrorAlertItem,
    Loading,
    StageCommentItem,
    FileAttachButton,
    WxcButton,
    FieldErrorItem
  },

  data: () => ({
    title: "GST Copy",
    loading: false,
    showRefreshItem: false,
    showUI: false,
    errorAlertEvent: false,
    networkError: {},
    gstPresent: true,
    gstPresentText: "Is GST applicable in your business? If yes, please upload the GST copy",
    invalidFields: [],
    gstDetails: {
      gst_number: "",
      office_name: "",
      gst_copy_uploaded: false
    },
    gstCopyRequired:true,
    uploadGstCopyText : "Upload GST Copy"
  }),

  methods: {
    showToast(message) {
      AlertService.showToast(message);
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    onInputEntered(key, value) {
      this.gstDetails[key] = value;
      this.removeFromInvalidFieldsIfValid(key);
    },

    onGSTEntered(value) {
      const gstNumber = value ? value.toString().toUpperCase() : value;
      this.onInputEntered("gst_number", gstNumber);
    },

    removeFromInvalidFieldsIfValid(key) {
      this.invalidFields = DynamicFieldUtils.removeFromInvalidFieldsIfValidValue(
        this.invalidFields,
        key,
        this.gstDetails[key],
        ALL_FIELDS_DATA,
        ALL_FIELDS_DATA
      );
    },

    getFieldLabel(key) {
      return ALL_FIELDS_DATA[key]["label"];
    },

    showFieldError(fieldKey) {
      return this.invalidFields && this.invalidFields.includes(fieldKey);
    },

    getFieldValue(key) {
      return this.gstDetails ? this.gstDetails[key] : "";
    },

    getErrorMsg(fieldKey) {
      return ALL_FIELDS_DATA[fieldKey]["errorMsg"];
    },

    onGSTPresentCheckboxChanged() {
      this.gstPresent = !this.gstPresent;
      if (this.gstPresent) {
        ALL_FIELDS_DATA.gst_number.optional = false;
        ALL_FIELDS_DATA.gst_copy_uploaded.optional = false;
      } else {
        ALL_FIELDS_DATA.gst_number.optional = true;
        ALL_FIELDS_DATA.gst_copy_uploaded.optional = true;
      }
    },

    onGSTAttached(e) {
      const file = e.files[0];
      this.loading = true;
      DataSource.createDocument(
        EntityType.LOANS,
        LoanScopeDataHolder.getLoanId(),
        DocumentDescription.GST_COPY.name,
        DocumentDescription.GST_COPY.description,
        DocumentDescription.GST_COPY.directory,
        file
      )
        .then(createDocumentRes => {
          this.loading = false;
          if (createDocumentRes.errorOccurred) {
            this.showError(createDocumentRes.data);
          } else {
            this.onInputEntered("gst_copy_uploaded", true);
            this.showToast(DOCUMENT_UPLOADED);
          }
        })
    },

    onSubmit() {
      this.invalidFields = [];
      if (this.checkForValidation()) {
        const attributes = [];
        attributes.push(AttributeUtil.getSingleAttributeData(
          AttributeDetails.ON_BOARDING_OFFICE_NAME.key,
          AttributeDetails.ON_BOARDING_OFFICE_NAME.name,
          this.gstDetails.office_name
        ));

        attributes.push(AttributeUtil.getSingleAttributeData(
          AttributeDetails.SKIP_TASK.key.replace("%s", this.currentTaskName),
          AttributeDetails.SKIP_TASK.name.replace("%s", this.currentTaskName),
          !this.gstPresent
        ));

        if (this.gstPresent) {
          attributes.push(AttributeUtil.getSingleAttributeData(
            AttributeDetails.DECLARED_GST.key,
            AttributeDetails.DECLARED_GST.name,
            this.gstDetails.gst_number
          ));
        }

        this.loading = true;
        DataSource.createOrUpdateAttribute(
          AttributeUtil.getAttributeUpdateDtoFromList(
            EntityType.LOANS,
            LoanScopeDataHolder.getLoanId(),
            attributes
          )
        )
          .then(res => {
            this.loading = false;
            if (res.errorOccurred) {
              this.showError(res.data);
            } else {
              this.completeCurrentTaskAndGetNextTask();
            }
          })
      }
    },

    checkForValidation() {
      if(!this.gstCopyRequired){
        ALL_FIELDS_DATA.gst_copy_uploaded.optional = true;
      }
      this.invalidFields = DynamicFieldUtils.validateDynamicFieldsForRequiredFields(
        this.gstDetails,
        ALL_FIELDS_DATA,
        ALL_FIELDS_DATA
      );

      return !this.invalidFields || this.invalidFields.length === 0;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getAttributes(EntityType.LOANS, LoanScopeDataHolder.getLoanId())
        .then(attributeRes => {
          this.loading = false;
          if (attributeRes.errorOccurred) {
            this.showError(attributeRes.data);
            this.showRefreshItem = true;
          } else {
            const attributes = attributeRes.data;
            this.gstDetails.office_name = LoanScopeDataHolder.getName();
            attributes && attributes.forEach(attribute => {
              if (attribute.key === AttributeDetails.ON_BOARDING_OFFICE_NAME.key) {
                this.onInputEntered("office_name", attribute.value);
              }
              if (attribute.key === AttributeDetails.DECLARED_GST.key) {
                this.onGSTEntered(attribute.value);
              }
            });
            LoanTasksUtility.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.tasks)
            .then(stageConfigDataRes => {
              if (!stageConfigDataRes.errorOccurred) {
                const gstCopyStageConfig = stageConfigDataRes.data;
                if(gstCopyStageConfig){
                  this.gstCopyRequired = gstCopyStageConfig.gstCopyRequired;
                  if(!this.gstCopyRequired){
                    this.uploadGstCopyText = "Upload GST Copy (optional)";
                  }
                }
                this.showUI = true;
              }
            });
          }
      });
      }
  },

  created() {
    this.fetchInitialData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.GST_COPY];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    documentAttachImage() {
      return this.gstDetails.gst_copy_uploaded
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.TICK_GREEN)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
    },

    checkboxImage() {
      return this.gstPresent
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.CHECKED_CHECKBOX)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.UNCHECKED_CHECKBOX);
    },

    attachFileButtonTextStyle() {
      return { color: "#ffffff" };
    },

    attachFileButtonStyle() {
      return {
        "background-color": "#f87246",
        "border-width": "1px",
        "border-style": "solid",
        "border-color": "gray"
      };
    }
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>

